import { MARKER_TYPE_TABLE, type PoiCategory } from "components/map/markerVisualTokens";
import icons from "./markerIcons.json";
import type { PoiGroup } from "./mapModel";

/**
 * A partner's synced Stippl list, as the server loader reads it (graphql/helpers/partnerMapLoader.ts), and what every
 * partner page does with it the same way. The pages (`/p/:partner`, `/mad-monkey/hostels/:hostelSlug`) get it as
 * `initialData`; null means the list does not exist yet (or reading it failed) and the page renders its static content.
 *
 * THE LIST DECIDES, THE STATIC CONTENT DRESSES
 * The list decides which places the page shows, in what order, where they are and what the line about each says. A
 * place the page's static content already knows (the same slug, or the same name) keeps the presentation that content
 * gave it: the partner-approved name, the hand-picked marker, label and photo. A place it does not know is drawn from
 * the list alone, its marker through the app's own table (`MARKER_TYPE_TABLE`). So the day a list lands the page looks
 * the same, and every later edit to the list still reaches it.
 */

// Mirrors `PartnerListItem` / `PartnerListData` / `PartnerPhotoCredit` in graphql/helpers/partnerMapLoader.ts: change both.
/** What a photo's licence asks the page to show, links https only. The loader sends none for Pexels, Unsplash or Nuitee photos. */
export interface PhotoCredit {
	/** the app's key for the source: "wikimedia", "venue" */
	source: string;
	author: string | null;
	/** "CC BY-SA 4.0" */
	licence: string | null;
	/** the licence's Creative Commons page */
	licenceUrl: string | null;
	authorUrl: string | null;
	/** the photo's own page */
	url: string | null;
}

export interface PartnerListItem {
	/** the last segment of the place's knowledge-base key; null for a place the sync did not make */
	slug: string | null;
	name: string;
	lat: number;
	lng: number;
	chapter: string;
	/** the marker group the chapter names ("Things to do", "Outdoors", "Eat & drink"), or null */
	group: PoiGroup | null;
	/** the list's line for the place, "" for none */
	line: string;
	hero: boolean;
	/** the app's icon slug on the place: a key of MARKER_TYPE_TABLE, or not */
	icon: string | null;
	/** "activity", "cuisine" or "accommodation" */
	attraction: string | null;
	/** the knowledge base's label, "Must-see", "Classic" */
	label: string | null;
	googlePlaceId: string | null;
	photo: string | null;
	/** null for a photo that needs no credit */
	credit: PhotoCredit | null;
}

export interface PartnerListData {
	/** the route param the server loaded the list for */
	slug: string;
	listId: string;
	items: PartnerListItem[];
}

/**
 * The list for this page, or null. `initialData` reaches every page of the route the server rendered, so a hostel page
 * reached by client-side navigation from another hostel's would otherwise get that hostel's list: the slug must match.
 */
export function liveListFor(data: unknown, slug: string): PartnerListData | null {
	const live = data as Partial<PartnerListData> | null | undefined;
	if (!live || typeof live !== "object" || live.slug !== slug || typeof live.listId !== "string" || !Array.isArray(live.items)) return null;
	return live as PartnerListData;
}

const MARKER_ICONS = icons as Record<string, string>;

const own = (table: object, key: string | null): key is string => !!key && Object.prototype.hasOwnProperty.call(table, key);

const CATEGORY_GROUP: Record<PoiCategory, PoiGroup> = { "things-to-do": "do", "food-drink": "food", outdoors: "out", hotels: "do" };

/** the drawing of a group when the place's own type has none on this page */
const GROUP_ICON: Record<PoiGroup, string> = { do: "star", food: "eat", out: "mountain" };

/**
 * A place's marker from the list alone: the group from its chapter, else from the app's marker table, else from what it
 * is; the drawing from its type when this page has that drawing (`markerIcons.json`), else its group's.
 */
export function markerOf(item: Pick<PartnerListItem, "group" | "icon" | "attraction">): { grp: PoiGroup; icon: string } {
	const key = own(MARKER_TYPE_TABLE, item.icon) ? item.icon : own(MARKER_TYPE_TABLE, item.attraction) ? item.attraction : null;
	const entry = key ? MARKER_TYPE_TABLE[key] : undefined;
	const grp: PoiGroup = item.group ?? (entry ? CATEGORY_GROUP[entry.category] : item.attraction === "cuisine" ? "food" : "do");
	const drawing = entry ? entry.icon.replace(/-marker--chunky$/, "") : null;
	return { grp, icon: own(MARKER_ICONS, drawing) ? drawing : GROUP_ICON[grp] };
}

const nameKey = (name: string): string =>
	name
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.trim();

/** The static entry for a list place: the same slug, else the same name (case, accents and punctuation aside). */
export function staticMatch<T extends { name: string; slug?: string }>(item: Pick<PartnerListItem, "slug" | "name">, entries: readonly T[]): T | undefined {
	const bySlug = item.slug ? entries.find((entry) => entry.slug === item.slug) : undefined;
	if (bySlug) return bySlug;
	const key = nameKey(item.name);
	return key ? entries.find((entry) => nameKey(entry.name) === key) : undefined;
}

/** Directions to the place in Google Maps: by its Google place id when it has one, at its coordinates either way. */
export const googleMapsUrl = ({ lat, lng, googlePlaceId }: Pick<PartnerListItem, "lat" | "lng" | "googlePlaceId">): string =>
	`https://www.google.com/maps/search/?api=1&query=${lat},${lng}${googlePlaceId ? `&query_place_id=${encodeURIComponent(googlePlaceId)}` : ""}`;
